import { getApiUrl } from '../config/domains';

const API_URL = getApiUrl();

// Type definitions for authentication
export interface User {
  id: number;
  email: string;
  username?: string;
  first_name?: string;
  last_name?: string;
  status: string;
  is_superuser: boolean;
  is_verified: boolean;
  created_at: string;
  updated_at: string;
  last_login?: string;
  roles: string[];
}

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface RegisterData {
  email: string;
  password: string;
  username?: string;
  first_name?: string;
  last_name?: string;
}

export interface TokenResponse {
  access_token: string;
  token_type: string;
  expires_in: number;
}

export interface PasswordResetRequest {
  email: string;
}

export interface PasswordResetConfirm {
  token: string;
  new_password: string;
}

export interface PasswordChange {
  current_password: string;
  new_password: string;
}

export interface AuthApiResponse<T> {
  data?: T;
  error?: string;
}

// Storage keys
const TOKEN_KEY = 'intrnauts_auth_token';
const TOKEN_EXPIRY_KEY = 'intrnauts_token_expiry';

// Token management
export const getStoredToken = (): string | null => {
  const token = localStorage.getItem(TOKEN_KEY);
  const expiry = localStorage.getItem(TOKEN_EXPIRY_KEY);

  if (!token || !expiry) return null;

  // Check if token is expired
  if (new Date().getTime() > parseInt(expiry)) {
    clearStoredToken();
    return null;
  }

  return token;
};

export const setStoredToken = (token: string, expiresIn: number): void => {
  const expiryTime = new Date().getTime() + (expiresIn * 1000);
  localStorage.setItem(TOKEN_KEY, token);
  localStorage.setItem(TOKEN_EXPIRY_KEY, expiryTime.toString());
};

export const clearStoredToken = (): void => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(TOKEN_EXPIRY_KEY);
};

// API helper function with authentication
async function authFetch<T>(
  endpoint: string,
  options?: RequestInit
): Promise<AuthApiResponse<T>> {
  try {
    const token = getStoredToken();
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    // Merge existing headers if provided
    if (options?.headers) {
      Object.entries(options.headers).forEach(([key, value]) => {
        if (typeof value === 'string') {
          headers[key] = value;
        }
      });
    }

    // Add authorization header if token exists
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(`${API_URL}${endpoint}`, {
      ...options,
      headers,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const errorMessage = errorData.detail || `API Error: ${response.statusText}`;
      console.error(`Auth API error (${response.status}):`, errorMessage);
      throw new Error(errorMessage);
    }

    const data = await response.json();
    return { data };
  } catch (error) {
    console.error('Auth API error:', error);
    return { error: error instanceof Error ? error.message : 'Unknown error' };
  }
}

// Authentication API calls
export const register = async (data: RegisterData): Promise<AuthApiResponse<User>> => {
  return authFetch<User>('/api/users/register', {
    method: 'POST',
    body: JSON.stringify(data),
  });
};

export const login = async (credentials: LoginCredentials): Promise<AuthApiResponse<TokenResponse>> => {
  const response = await authFetch<TokenResponse>('/api/users/login', {
    method: 'POST',
    body: JSON.stringify(credentials),
  });

  // Store token if login successful
  if (response.data) {
    setStoredToken(response.data.access_token, response.data.expires_in);
  }

  return response;
};

export const logout = (): void => {
  clearStoredToken();
};

export const getCurrentUser = async (): Promise<AuthApiResponse<User>> => {
  return authFetch<User>('/api/users/me');
};

export const updateProfile = async (data: Partial<User>): Promise<AuthApiResponse<User>> => {
  return authFetch<User>('/api/users/me', {
    method: 'PUT',
    body: JSON.stringify(data),
  });
};

export const changePassword = async (data: PasswordChange): Promise<AuthApiResponse<any>> => {
  return authFetch('/api/users/change-password', {
    method: 'POST',
    body: JSON.stringify(data),
  });
};

export const requestPasswordReset = async (data: PasswordResetRequest): Promise<AuthApiResponse<any>> => {
  return authFetch('/api/users/password-reset/request', {
    method: 'POST',
    body: JSON.stringify(data),
  });
};

export const confirmPasswordReset = async (data: PasswordResetConfirm): Promise<AuthApiResponse<any>> => {
  return authFetch('/api/users/password-reset/confirm', {
    method: 'POST',
    body: JSON.stringify(data),
  });
};

// Admin API calls
export const getAllUsers = async (): Promise<AuthApiResponse<User[]>> => {
  return authFetch<User[]>('/api/users/');
};

export const getUserById = async (userId: number): Promise<AuthApiResponse<User>> => {
  return authFetch<User>(`/api/users/${userId}`);
};

export const deleteUser = async (userId: number): Promise<AuthApiResponse<any>> => {
  return authFetch(`/api/users/${userId}`, {
    method: 'DELETE',
  });
};

// Comment API calls
export interface Comment {
  id: number;
  post_slug: string;
  user_id: number;
  user_email: string;
  content: string;
  created_at: string;
  updated_at: string;
}

export const getComments = async (slug: string): Promise<AuthApiResponse<Comment[]>> => {
  return authFetch<Comment[]>(`/api/blog/posts/${slug}/comments`);
};

export const createComment = async (
  slug: string,
  content: string
): Promise<AuthApiResponse<Comment>> => {
  return authFetch<Comment>(`/api/blog/posts/${slug}/comments`, {
    method: 'POST',
    body: JSON.stringify({ content }),
  });
};

export const updateComment = async (
  commentId: number,
  content: string
): Promise<AuthApiResponse<Comment>> => {
  return authFetch<Comment>(`/api/blog/comments/${commentId}`, {
    method: 'PUT',
    body: JSON.stringify({ content }),
  });
};

export const deleteComment = async (commentId: number): Promise<AuthApiResponse<any>> => {
  return authFetch(`/api/blog/comments/${commentId}`, {
    method: 'DELETE',
  });
};
